import Phaser from 'phaser';
import { DEPTHS, yDepth } from '../config/gameConfig';
import { THEME } from '../config/theme';
import { Accessibility } from '../setup';
import type { Level } from '../config/levels';
import type { LevelGeometry } from './LevelGeometry';
import type { ObstacleRect } from './WildlifeSystem';
import { type ColorTransform, dayColors } from '../utils/nightPalette';
import type { SeededRNG } from '../utils/seededRNG';

/**
 * Creates obstacles, interactable buildings (restaurant, fuel station),
 * and decorative chalets. Returns building footprints for wildlife collision.
 */
export class ObstacleBuilder {
  private scene: Phaser.Scene;
  private geometry: LevelGeometry;
  private nightSfx: string;
  private nc: ColorTransform;
  private rng: SeededRNG | null;

  buildingRects: ObstacleRect[] = [];

  constructor(scene: Phaser.Scene, geometry: LevelGeometry, nightSfx = '', nc: ColorTransform = dayColors, rng: SeededRNG | null = null) {
    this.scene = scene;
    this.geometry = geometry;
    this.nightSfx = nightSfx;
    this.nc = nc;
    this.rng = rng;
  }

  /**
   * Create all obstacles and interactable buildings.
   * Call after physics groups are created.
   */
  create(
    level: Level, tileSize: number,
    obstacles: Phaser.Physics.Arcade.StaticGroup,
    interactables: Phaser.Physics.Arcade.StaticGroup,
    avoidPoints?: { x: number; y: number; radius: number }[]
  ): void {
    this.buildingRects = [];
    const obstacleTypes = level.obstacles || [];
    const worldWidth = level.width * tileSize;
    const worldHeight = level.height * tileSize;
    const isStorm = level.weather === 'storm';

    // Difficulty-scaled obstacle count — kept low for realistic pistes
    const baseCount = Math.floor(level.width * level.height / 100);
    let difficultyMultiplier: number;
    switch (level.difficulty) {
      case 'tutorial': difficultyMultiplier = 0.1; break;
      case 'green': difficultyMultiplier = 0.2; break;
      case 'blue': difficultyMultiplier = 0.3; break;
      case 'red': difficultyMultiplier = 0.5; break;
      case 'black': difficultyMultiplier = 0.6; break;
      case 'park': difficultyMultiplier = 0.2; break;
      default: difficultyMultiplier = 0.3;
    }
    const obstacleCount = Math.floor(baseCount * difficultyMultiplier);

    const placedPositions: { x: number; y: number }[] = [];
    const minSpacing = tileSize * 6; // comfortable gap for groomer to pass between any two
    const minSpacingSq = minSpacing * minSpacing;

    for (let i = 0; i < obstacleCount; i++) {
      const type = this.rng ? this.rng.pick(obstacleTypes) : Phaser.Utils.Array.GetRandom(obstacleTypes);
      if (!type) continue;

      let x: number, y: number;
      let attempts = 0;
      const tooClose = (px: number, py: number) => {
        if (avoidPoints?.some(p => {
          const dx = px - p.x, dy = py - p.y;
          return dx * dx + dy * dy < p.radius * p.radius;
        })) return true;
        // Enforce minimum spacing between obstacles
        return placedPositions.some(p => {
          const dx = px - p.x, dy = py - p.y;
          return dx * dx + dy * dy < minSpacingSq;
        });
      };
      do {
        if (this.rand() < 0.9) {
          // Piste edges — where real obstacles naturally sit
          if (this.rand() < 0.5) {
            x = this.between(tileSize * 3, tileSize * 6);
          } else {
            x = this.between(worldWidth - tileSize * 6, worldWidth - tileSize * 3);
          }
          y = this.between(tileSize * 5, worldHeight - tileSize * 5);
        } else {
          x = this.between(tileSize * 8, worldWidth - tileSize * 8);
          y = this.between(tileSize * 10, worldHeight - tileSize * 10);
        }
        attempts++;
      } while ((this.geometry.isOnAccessPath(x, y) || this.geometry.isOnCliff(x, y) || tooClose(x, y)) && attempts < 30);
      if (this.geometry.isOnAccessPath(x, y) || this.geometry.isOnCliff(x, y) || tooClose(x, y)) continue;

      let texture = 'tree';
      if (type === 'rocks') texture = 'rock';
      texture += this.nightSfx;

      const obstacle = obstacles.create(x, y, texture);
      obstacle.setImmovable(true);
      obstacle.setScale(tileSize / 16);
      obstacle.setDepth(yDepth(y));
      placedPositions.push({ x, y });

      if (isStorm) {
        const s = tileSize / 16;
        const sg = this.scene.add.graphics().setDepth(yDepth(y) + 0.0001);
        sg.fillStyle(this.nc(THEME.colors.snowCap), 1);
        if (type === 'rocks') {
          sg.fillRect(x - 10 * s, y - 6 * s, 20 * s, 3 * s);
        } else {
          // Tree: snow on top two foliage tiers
          sg.fillRect(x - 5 * s, y - 20 * s, 10 * s, 2 * s);
          sg.fillRect(x - 9 * s, y - 12 * s, 18 * s, 2 * s);
        }
      }
    }

    // Restaurant at top of level
    const restaurant = interactables.create(
      worldWidth / 2 - tileSize * 4, tileSize * 2, 'restaurant' + this.nightSfx
    );
    restaurant.interactionType = 'food';
    restaurant.setScale(tileSize / 16);
    restaurant.setDepth(yDepth(restaurant.y));
    const rSize = tileSize * 2;
    this.addFootprint(restaurant.x, restaurant.y, rSize, rSize);

    if (isStorm) {
      const s = tileSize / 16;
      const rg = this.scene.add.graphics().setDepth(yDepth(restaurant.y) + 0.0001);
      rg.fillStyle(this.nc(THEME.colors.snowCap), 1);
      rg.fillRect(restaurant.x - 28 * s, restaurant.y - 25 * s, 56 * s, 3 * s);
    }

    // Fuel station at bottom of level — placed on-piste to avoid boundary collisions
    const fuelRow = Math.max(0, level.height - 3);
    const bottomPath = this.geometry.pistePath[fuelRow];
    let fuelX: number;
    if (bottomPath) {
      const pisteRight = (bottomPath.centerX + bottomPath.width / 2) * tileSize;
      // Park: hug the right boundary edge to stay clear of features; others: inset more
      fuelX = level.difficulty === 'park'
        ? pisteRight - tileSize
        : pisteRight - tileSize * 2;
    } else {
      fuelX = worldWidth / 2 + tileSize * 4;
    }
    const fuelStation = interactables.create(
      fuelX, worldHeight - tileSize * 3, 'fuel' + this.nightSfx
    );
    fuelStation.interactionType = 'fuel';
    fuelStation.setScale(tileSize / 16);
    fuelStation.setDepth(yDepth(fuelStation.y));
    this.addFootprint(fuelStation.x, fuelStation.y, tileSize * 2, tileSize * 2);

    if (isStorm) {
      const s = tileSize / 16;
      const fg = this.scene.add.graphics().setDepth(yDepth(fuelStation.y) + 0.0001);
      fg.fillStyle(this.nc(THEME.colors.snowCap), 1);
      fg.fillRect(fuelStation.x - 16 * s, fuelStation.y - 20 * s, 28 * s, 3 * s);
    }

    // Chalets on easier pistes
    if (['tutorial', 'green', 'blue'].includes(level.difficulty)) {
      this.createResortBuildings(level, tileSize, worldWidth);
    }
  }

  /** Register a building footprint (center-based) for wildlife collision avoidance. */
  private addFootprint(cx: number, cy: number, w: number, h: number): void {
    this.buildingRects.push({ x: cx - w / 2, y: cy - h / 2, w, h });
  }

  private createResortBuildings(level: Level, tileSize: number, worldWidth: number): void {
    let chaletCount: number;
    switch (level.difficulty) {
      case 'tutorial': chaletCount = 3; break;
      case 'green': chaletCount = 4; break;
      case 'blue': chaletCount = 2; break;
      default: chaletCount = 1;
    }

    const isTutorial = level.difficulty === 'tutorial';

    for (let i = 0; i < chaletCount; i++) {
      const side = i % 2 === 0 ? 'left' : 'right';

      let yPos: number;
      if (isTutorial) {
        yPos = tileSize * (4 + i * 2.5);
      } else {
        const bottomY = level.height * tileSize;
        yPos = bottomY - tileSize * (8 + i * 3);
      }

      const pathIndex = Math.floor(yPos / tileSize);
      const path = this.geometry.pistePath[pathIndex] ||
        { centerX: level.width / 2, width: level.width * 0.5 };

      const pisteEdge = side === 'left' ?
        (path.centerX - path.width / 2) * tileSize :
        (path.centerX + path.width / 2) * tileSize;

      const x = side === 'left' ?
        Math.max(tileSize * 3, pisteEdge - tileSize * 4) :
        Math.min(worldWidth - tileSize * 3, pisteEdge + tileSize * 4);

      // Skip if chalet would overlap an existing building
      const cSize = tileSize * 2;
      const cx = x - cSize / 2, cy = yPos - cSize * 0.4;
      const cw = cSize, ch = cSize * 0.65;
      const overlaps = this.buildingRects.some(b =>
        cx < b.x + b.w && cx + cw > b.x && cy < b.y + b.h && cy + ch > b.y
      );
      if (overlaps) continue;

      this.createChalet(x, yPos, tileSize, level.weather === 'storm');
    }
  }

  private createChalet(x: number, y: number, tileSize: number, isStorm?: boolean): void {
    const g = this.scene.add.graphics();
    const size = tileSize * 2;
    g.setDepth(yDepth(y + size * 0.25));

    this.addFootprint(x, y - size * 0.4 + size * 0.325, size, size * 0.65);

    // Chalet body (wooden)
    g.fillStyle(this.nc(THEME.colors.woodBrown), 1);
    g.fillRect(x - size / 2, y - size * 0.4, size, size * 0.6);

    // Stone foundation
    g.fillStyle(this.nc(THEME.colors.metalGrey), 1);
    g.fillRect(x - size / 2 - 2, y + size * 0.15, size + 4, size * 0.1);

    // Roof (dark wood with snow)
    g.fillStyle(this.nc(THEME.colors.woodDark), 1);
    g.beginPath();
    g.moveTo(x - size * 0.7, y - size * 0.35);
    g.lineTo(x, y - size * 0.8);
    g.lineTo(x + size * 0.7, y - size * 0.35);
    g.closePath();
    g.fillPath();

    // Snow on roof
    g.fillStyle(this.nc(THEME.colors.snowflake), 0.9);
    g.beginPath();
    g.moveTo(x - size * 0.65, y - size * 0.4);
    g.lineTo(x, y - size * 0.75);
    g.lineTo(x + size * 0.65, y - size * 0.4);
    g.lineTo(x + size * 0.5, y - size * 0.35);
    g.lineTo(x, y - size * 0.6);
    g.lineTo(x - size * 0.5, y - size * 0.35);
    g.closePath();
    g.fillPath();

    // Storm: extra snow buildup on roof and chimney
    if (isStorm) {
      g.fillStyle(this.nc(THEME.colors.snowCap), 1);
      g.fillRect(x - size * 0.55, y - size * 0.42, size * 1.1, size * 0.06);
      g.fillRect(x + size * 0.24, y - size * 0.75, size * 0.14, size * 0.04);
    }

    // Windows
    g.fillStyle(this.nc(THEME.colors.skyBlue), 1);
    g.fillRect(x - size * 0.3, y - size * 0.25, size * 0.2, size * 0.2);
    g.fillRect(x + size * 0.1, y - size * 0.25, size * 0.2, size * 0.2);

    // Door
    g.fillStyle(this.nc(THEME.colors.woodDark), 1);
    g.fillRect(x - size * 0.1, y - size * 0.05, size * 0.2, size * 0.25);

    // Chimney with smoke
    g.fillStyle(this.nc(THEME.colors.metalGrey), 1);
    g.fillRect(x + size * 0.25, y - size * 0.7, size * 0.12, size * 0.2);

    if (!Accessibility.settings.reducedMotion) {
      g.fillStyle(this.nc(THEME.colors.metalLight), 0.6);
      g.fillCircle(x + size * 0.31, y - size * 0.8, 3);
      g.fillCircle(x + size * 0.28, y - size * 0.9, 2);
    }
  }

  reset(): void {
    this.buildingRects = [];
  }

  /** Random float 0–1, seeded if available. */
  private rand(): number {
    return this.rng ? this.rng.frac() : Math.random();
  }

  /** Random integer in [min, max], seeded if available. */
  private between(min: number, max: number): number {
    return this.rng ? this.rng.integerInRange(min, max) : Phaser.Math.Between(min, max);
  }
}
